<template>
  <div class="menu-container" :class="`menu-position--${position}`">
    <div ref="button">
      <slot name="button"></slot>
    </div>

    <div class="menu" ref="menu" v-if="isOpen">
      <div class="menu--user-info">
        <span>{{ userStore.userData?.name }}</span>
        <div>
          {{ userStore.userData?.email }}
        </div>
      </div>
      <ul class="menu-user-links">
        <li>
          <RouterLink to="/user-profile">
            <UserProfile />
            User Profile
          </RouterLink>
        </li>
        <li>
          <RouterLink to="/logout">
            <UserLogout />
            Sign Out
          </RouterLink>
        </li>
      </ul>
      <div class="menu-versions">
        <div v-if="version != ''">App version {{ version }}</div>
        <div v-if="apiStore.apiVersion != ''">API version {{ apiStore.apiVersion }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import UserProfile from '@/icons/UserProfile.svg?component';
import UserLogout from '@/icons/UserLogout.svg?component';
import packageJson from '../../../package.json';

import { useUserStore } from '@/stores/user';
import { useApiStore } from '@/stores/api';
const userStore = useUserStore();
const apiStore = useApiStore();
const version = packageJson.version ?? '';

const isOpen = ref(false);
const menu = ref();
const button = ref();

defineProps<{
  position: string;
}>();

const listener = (event: Event) => {
  if (event.target === button.value || event.composedPath().includes(button.value)) {
    if (isOpen.value) {
      isOpen.value = false;
    } else {
      isOpen.value = true;
    }
  } else if (event.target === menu.value || event.composedPath().includes(menu.value)) {
    isOpen.value = true;
  } else {
    isOpen.value = false;
  }
};

onBeforeUnmount(() => {
  if (window) {
    window.removeEventListener('click', listener);
  }
});

onMounted(() => {
  if (window) {
    window.addEventListener('click', listener);
  }
});
</script>

<style lang="scss">
.menu-container {
  position: relative;

  .menu {
    @include sizing.padding('xs');
    @include mixins.rounded-border($color: variables.$primitives-color-border-100);
    background: variables.$primitives-color-background-100;
    box-shadow: 0px 2px 10px 0px #0000000d;
    min-width: 200px;
    position: absolute;
    width: 100%;
    z-index: 100;
  }

  &.menu-position--top .menu {
    bottom: 100%;
    left: 0;
  }

  &.menu-position--bottom .menu {
    right: 0;
    top: 100%;
  }

  .menu--user-info {
    @include sizing.padding('xxxs');
    @include typography.font-body($size: sm, $weight: medium);
    border-bottom: 1px solid variables.$primitives-color-border-200;
    color: variables.$primitives-color-tertiary-500;
    text-transform: uppercase;

    span,
    div {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    div {
      color: variables.$primitives-color-tertiary-400;
      font-weight: normal;
      text-transform: none;
    }
  }

  .menu-user-links {
    @include sizing.margin('xxxs', 0);
    list-style-type: none;

    li a {
      @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxs);
      @include sizing.padding('xxs', 'xxxs');
      color: variables.$primitives-color-tertiary-500;

      &:hover {
        color: variables.$primitives-color-tertiary-300;
      }
    }

    li:last-of-type a {
      padding-bottom: variables.$sizing-xxxs;
    }
  }

  .menu-versions {
    @include sizing.padding('xxxs');
    @include typography.font-body($size: sm, $weight: normal);
    border-top: 1px solid variables.$primitives-color-border-200;
    color: variables.$primitives-color-tertiary-400;
  }
}
</style>
